import { DEFAULT_LOCALE, VALID_LOCALES } from '@/constants';
import { normalizeCanonicalUrl } from './normalizeCanonicalUrl';

/**
 * Path without query and without leading locale segment (locale from list).
 * Single source of truth for canonical and hreflang clean path.
 */
export function getCleanPath(
	path: string,
	localeList: readonly string[] | string[],
): string {
	const pathWithoutQuery = (path.split('?')[0] ?? '').trim();
	const withLeadingSlash = pathWithoutQuery.startsWith('/')
		? pathWithoutQuery
		: `/${pathWithoutQuery}`;
	const segments = withLeadingSlash.split('/').filter(Boolean);
	const firstSegment = segments[0];
	const localeSet = new Set(localeList.map(l => l.toLowerCase()));
	if (firstSegment && localeSet.has(firstSegment.toLowerCase())) {
		const rest = segments.slice(1).join('/');
		return rest ? `/${rest}` : '/';
	}
	return withLeadingSlash || '/';
}

/**
 * Builds the canonical URL for a given locale and path.
 * Single source of truth for canonical and hreflang URLs (no trailing slash for root).
 *
 * @param baseUrl - Base URL (e.g. 'https://liftapp.ai')
 * @param cleanPath - Path without query and without locale segment; with or without leading slash (e.g. '/' or '/image-upscaler' or 'image-upscaler')
 * @param locale - Locale code (e.g. 'tr', 'en')
 * @param defaultLocale - Default locale (defaults to 'en')
 */
export const getCanonicalUrlForLocale = (
	baseUrl: string,
	cleanPath: string,
	locale: string,
	defaultLocale = DEFAULT_LOCALE,
): string => {
	const normalizedBase = normalizeCanonicalUrl(baseUrl) || baseUrl;
	const pathWithoutLeadingSlash = cleanPath.replace(/^\//, '').trim();

	// if (!pathWithoutLeadingSlash) {
	// 	return normalizedBase;
	// }
	if (!pathWithoutLeadingSlash) {
		return locale !== defaultLocale
			? `${normalizedBase}/${locale}`
			: normalizedBase;
	}

	const localePrefix = locale !== defaultLocale ? `${locale}/` : '';
	return `${normalizedBase}/${localePrefix}${pathWithoutLeadingSlash}`;
};

/**
 * Extracts clean path (no query, no locale segment) from pathname.
 * Uses getCleanPath with VALID_LOCALES for consistency with HreflangTags.
 */
export const getCleanPathFromPathname = (pathname: string): string => {
	return getCleanPath(pathname, VALID_LOCALES);
};

/**
 * Builds a canonical URL with locale support.
 * @param baseUrl - Base URL (e.g., 'https://liftapp.ai')
 * @param pathname - Current pathname (e.g., '/tr/image-upscaler' or '/image-upscaler')
 * @param locale - Current locale (e.g., 'tr', 'en')
 * @param defaultLocale - Default locale (defaults to 'en')
 * @returns Canonical URL with locale prefix (if not default locale)
 */
export const buildCanonicalUrl = (
	baseUrl: string,
	pathname: string,
	locale: string,
	defaultLocale = DEFAULT_LOCALE,
): string => {
	const cleanPath = getCleanPath(pathname, VALID_LOCALES);
	return getCanonicalUrlForLocale(baseUrl, cleanPath, locale, defaultLocale);
};
